import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import SegmentedControl from '../../../components/base/SegmentedControl';
import PropertyTypePicker from '../../../components/search/PropertyTypePicker';
import RangeFilterGrid from '../../../components/search/RangeFilterGrid';
import LocationSearch from '../../../components/search/LocationSearch';
import type { SelectMenuOption } from '../../../components/base/SelectMenu';
import {
  getSearchFilterFields,
  getVisibleRangeKeys,
  rangesFromFilters,
} from '../../../config/searchFilterFields';
import type { LocationSuggestion } from '../../../services/neighborhoodMapService';
import { formatAmount } from '../../../utils/numberFormat';
import { defaultFilters } from '../urlState';
import {
  categoryChange,
  countActiveFilters,
  listingTypeChange,
  pricePeriodChange,
  rangeChange,
  typeChange,
} from '../filterActions';
import type { FilterState } from '../types';
import FilterPill from './FilterPill';

/**
 * The /search filter bar (owner, 2026-10-07), modelled on wasalt.sa: one row of
 * pills under the navigation instead of a long sidebar that showed every filter
 * at once.
 *
 *   location · للإيجار/للبيع/الكل · نوع العقار · السعر · الغرف · الترتيب · كل الفلاتر
 *
 * Each pill opens only its own controls, and a pill holding a value shows it, so
 * the bar doubles as the summary of what is filtered. «كل الفلاتر» opens the full
 * panel (PropertyFilters) in the drawer page.tsx owns: area, amenities, keyword
 * and landmark search live there. Below lg the bar keeps location + «كل الفلاتر»;
 * the phone sheet already holds everything.
 *
 * Sort stays WITH the filters, as decided in 2026-10 (it used to be in the
 * results toolbar): here it is the bar's last pill, and in the phone sheet it is
 * the first section.
 */

interface SortControl {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly SelectMenuOption[];
}

export default function SearchFilterBar({
  filters,
  onFiltersChange,
  onClearAll,
  onLocationSelect,
  locationChips,
  sort,
  onOpenAllFilters,
}: {
  filters: FilterState;
  onFiltersChange: (changes: Partial<FilterState>) => void;
  onClearAll: () => void;
  onLocationSelect: (loc: LocationSuggestion) => void;
  /** The chosen city / district chips (and the district radius), owned by page.tsx. */
  locationChips?: ReactNode;
  sort: SortControl;
  onOpenAllFilters: () => void;
}) {
  const { t, i18n } = useTranslation(['search', 'common']);
  const isArabic = i18n.language === 'ar';
  const baseId = useId().replace(/[^a-zA-Z0-9_-]/g, '');

  const fields = getSearchFilterFields(filters.propertyType, filters.category);
  const visibleRanges = getVisibleRangeKeys(filters.propertyType, filters.category);
  const roomKeys = visibleRanges.filter((k) => k === 'bedrooms' || k === 'bathrooms');
  const ranges = rangesFromFilters(filters);
  const activeCount = countActiveFilters(filters, defaultFilters);

  // ── pill labels: the filter's name, or the value chosen ──
  const typeLabel = filters.propertyType
    ? t(`search.filters.propertyTypeOptions.${filters.propertyType}`, { defaultValue: filters.propertyType })
    : filters.category === 'commercial'
      ? t('search.filters.categoryCommercial')
      : filters.category === 'residential'
        ? t('search.filters.categoryResidential')
        : t('search.filters.propertyTypeLabel');
  const typeActive = Boolean(filters.propertyType) || filters.category !== defaultFilters.category;

  const span = (min: string, max: string) => {
    const lo = formatAmount(min);
    const hi = formatAmount(max);
    if (lo && hi) return `${lo} - ${hi}`;
    if (lo) return `${lo}+`;
    return isArabic ? `حتى ${hi}` : `Up to ${hi}`;
  };
  const priceActive = Boolean(filters.minPrice || filters.maxPrice);
  const priceLabel = priceActive ? span(filters.minPrice, filters.maxPrice) : t('search.results.pricePill');

  const bedroomsName = isArabic ? fields.bedroomsLabelAr : fields.bedroomsLabelEn;
  const roomsActive = Boolean(filters.bedrooms || filters.bedroomsMax || filters.bathrooms || filters.bathroomsMax);
  const roomsLabel = filters.bedrooms || filters.bedroomsMax
    ? `${bedroomsName}: ${span(filters.bedrooms, filters.bedroomsMax)}`
    : roomKeys.includes('bedrooms')
      ? bedroomsName
      : t('search.filters.bathroomsLabel');

  const sortCurrent = sort.options.find((o) => o.value === sort.value);

  const doneButton = (close: () => void) => (
    <div className="mt-4 flex justify-end">
      <button
        type="button"
        onClick={close}
        className="inline-flex h-10 items-center rounded-full bg-[var(--pv-color-primary)] px-5 text-[0.875rem] font-semibold text-white transition-opacity hover:opacity-90 dark:text-[var(--pv-color-surface)]"
      >
        {t('search.results.done')}
      </button>
    </div>
  );

  return (
    <div className="border-b border-[var(--pv-border-subtle)] bg-[var(--pv-color-surface)]">
      <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-2 px-3 py-3 sm:px-4">
        {/* Phones: the search and «كل الفلاتر» share the first row. */}
        <div className="min-w-0 flex-1 basis-48 sm:w-72 sm:flex-none sm:basis-auto">
          <LocationSearch onSelect={onLocationSelect} />
        </div>
        {locationChips}

        {/* The single-purpose pills are a desktop affordance; on phones the sheet
            opened by «كل الفلاتر» (and the floating button) holds them all. */}
        <div className="hidden lg:contents">
          <SegmentedControl<FilterState['listingType']>
            value={filters.listingType}
            onChange={(listingType) => onFiltersChange(listingTypeChange(listingType))}
            options={[
              { value: 'rent', label: t('search.filters.listingTypeRent') },
              { value: 'buy', label: t('search.filters.listingTypeBuy') },
              { value: 'all', label: t('search.filters.listingTypeAll') },
            ]}
            ariaLabel={t('search.filters.listingTypeLabel')}
            size="sm"
          />

          <FilterPill label={typeLabel} active={typeActive} panelLabel={t('search.filters.propertyTypeLabel')} panelWidth="26rem">
            {(close) => (
              <div className="space-y-4">
                <SegmentedControl<FilterState['category']>
                  value={filters.category}
                  onChange={(category) => onFiltersChange(categoryChange(filters, category))}
                  options={[
                    { value: '', label: t('search.filters.categoryAll') },
                    { value: 'residential', label: t('search.filters.categoryResidential') },
                    { value: 'commercial', label: t('search.filters.categoryCommercial') },
                  ]}
                  ariaLabel={t('search.filters.categoryLabel')}
                  size="sm"
                  fullWidth
                />
                <PropertyTypePicker
                  variant="inline"
                  id={`${baseId}-type`}
                  value={filters.propertyType}
                  category={filters.category}
                  onChange={(propertyType, option) => {
                    onFiltersChange(typeChange(filters, propertyType, option));
                    close();
                  }}
                  label={t('search.filters.propertyTypeLabel')}
                  hideLabel
                  columns={2}
                  tileLayout="row"
                />
              </div>
            )}
          </FilterPill>

          {visibleRanges.includes('price') && (
            <FilterPill label={priceLabel} active={priceActive} panelLabel={t('search.results.pricePill')} panelWidth="24rem">
              {(close) => (
                <>
                  <RangeFilterGrid
                    only={['price']}
                    propertyType={filters.propertyType}
                    category={filters.category}
                    listingType={filters.listingType}
                    value={ranges}
                    onChange={(key, next) => onFiltersChange(rangeChange(key, next))}
                    layout="stacked"
                    idPrefix={`${baseId}-price`}
                    pricePeriod={filters.pricePeriod}
                    onPricePeriodChange={(period) => onFiltersChange(pricePeriodChange(period))}
                  />
                  {doneButton(close)}
                </>
              )}
            </FilterPill>
          )}

          {roomKeys.length > 0 && (
            <FilterPill label={roomsLabel} active={roomsActive} panelLabel={bedroomsName} panelWidth="24rem">
              {(close) => (
                <>
                  <RangeFilterGrid
                    only={roomKeys}
                    propertyType={filters.propertyType}
                    category={filters.category}
                    listingType={filters.listingType}
                    value={ranges}
                    onChange={(key, next) => onFiltersChange(rangeChange(key, next))}
                    layout="stacked"
                    idPrefix={`${baseId}-rooms`}
                  />
                  {doneButton(close)}
                </>
              )}
            </FilterPill>
          )}

          <FilterPill
            label={sortCurrent?.label ?? sort.label}
            icon={<i className="ri-sort-desc text-base leading-none" aria-hidden="true" />}
            panelLabel={sort.label}
            panelWidth="16rem"
          >
            {(close) => (
              <div role="radiogroup" aria-label={sort.label} className="-m-2 flex flex-col">
                {sort.options.map((option) => {
                  const checked = option.value === sort.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={checked}
                      onClick={() => {
                        sort.onChange(option.value);
                        close();
                      }}
                      className={[
                        'flex min-h-[2.75rem] items-center gap-2.5 rounded-lg px-3 text-start text-[0.875rem] transition-colors',
                        checked
                          ? 'bg-[var(--pv-color-surface-sunken)] font-semibold text-[var(--pv-color-primary)]'
                          : 'text-[var(--pv-color-text)] hover:bg-[var(--pv-color-surface-soft)]',
                      ].join(' ')}
                    >
                      <span className="text-base leading-none text-[var(--pv-color-text-muted)]" aria-hidden="true">{option.icon}</span>
                      <span className="flex-1">{option.label}</span>
                      {checked && <i className="ri-check-line text-base leading-none" aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
            )}
          </FilterPill>
        </div>

        <button
          type="button"
          onClick={onOpenAllFilters}
          className="inline-flex h-11 shrink-0 items-center gap-2 rounded-full border border-[var(--pv-border-strong)] bg-[var(--pv-color-surface)] px-4 text-[0.875rem] font-medium text-[var(--pv-color-text)] transition-colors hover:border-[var(--pv-color-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30"
        >
          <i className="ri-equalizer-line text-base leading-none" aria-hidden="true" />
          {t('search.results.allFilters')}
          {activeCount > 0 && (
            <span className="grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-[var(--pv-color-primary)] px-1 text-[0.6875rem] font-bold tabular-nums text-white dark:text-[var(--pv-color-surface)]">
              {activeCount}
            </span>
          )}
        </button>

        {activeCount > 0 && (
          <button
            type="button"
            onClick={onClearAll}
            className="inline-flex h-11 shrink-0 items-center rounded-full px-3 text-[0.875rem] font-medium text-[var(--pv-color-primary)] underline-offset-4 transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30"
          >
            {t('search.filters.clear')}
          </button>
        )}
      </div>
    </div>
  );
}
